import { Auth, Dashboard, Minimal, PublicFluid } from 'layouts';
import React, { Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Switch, Redirect } from 'react-router-dom';
import { AuthenticatedRoutes, PublicRoutes } from 'routes/index';
import usePageTracking from 'hooks/usePageTracking';
import DeferredLoadingPage from 'components/@2026/DeferredLoadingPage';

import {
  Home as HomeView,
  NotFoundCover as NotFoundCoverView,
  SigninCover as SigninCoverView,
  SignupCover as SignupCoverView,
  ResetPassword as ResetPasswordView,
  FirebaseEmailAction as FirebaseEmailActionView,
  ProfileSettings as ProfileSettingsView,
  Preferences as PreferencesView,
  OrgSettings as OrgSettingsView,
  SalesOrg as SalesOrgView,
  Integrations as IntegrationsView,
  SomethingWentWrong as SomethingWentWrongView,
  Unsubscribe as UnsubscribeView,
  TeamSettings as TeamSettingsView,
  RecallAlternative as RecallAlternativeView,
  GoogleCalendarConnect as GoogleCalendarConnectView,
  ZohoCrmConnect as ZohoCrmConnectView,
  PrivacyPolicy as PrivacyPolicyView,
  ActionPlan as ActionPlanView,
  MeetingPrepPublic as MeetingPrepPublicView,
  Deals as DealsView,
  UnlinkedItems as UnlinkedItemsView,
  Mailbox as MailboxView,
  DealPage as DealPageView,
  Companies as CompaniesView,
  CompanyPage as CompanyPageView,
  Contacts as ContactsView,
  ContactPage as ContactPageView,
  Reports as ReportsView,
  FeaturePreview as FeaturePreviewView,
  ExpertConversation as ExpertConversationView,
  GmailIntegrationHelp as GmailIntegrationHelpView,
  OpenAiIntegrationHelp as OpenAiIntegrationHelpView,
  DeepLinkValidation as DeepLinkValidationView,
  GmailOAuthConnect as GmailOAuthConnectView,
  OutlookCalendarOAuthConnect as OutlookCalendarOAuthConnectView,
  OutlookEmailOAuthConnect as OutlookEmailOAuthConnectView,
  PipedriveOAuthConnect as PipedriveOAuthConnectView,
  ZoomOAuthConnect as ZoomOAuthConnectView,
  UnipileOAuthConnect as UnipileOAuthConnectView,
  SlackOAuthConnect as SlackOAuthConnectView,
  Pricing as PricingView,
  OAuthLogin as OAuthLoginView,
  OAuthConsent as OAuthConsentView,
} from 'views/lazy';

const Routes = () => {
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);

  usePageTracking();

  return (
    <Suspense fallback={<DeferredLoadingPage />}>
      <Switch>
        {/* -----------Authentication Routes---------  */}
        <PublicRoutes
          exact
          current={'signin'}
          component={SigninCoverView}
          path="/auth/signin"
          layout={Auth}
        />
        <PublicRoutes
          exact
          current={'signup'}
          component={SignupCoverView}
          path="/auth/signup"
          layout={Auth}
        />
        <PublicRoutes
          exact
          current={'resetPassword'}
          component={ResetPasswordView}
          path="/reset/password"
          layout={Auth}
        />
        <PublicRoutes
          exact
          current={'emailAction'}
          component={FirebaseEmailActionView}
          path="/email/action"
          layout={Auth}
        />
        {/* -----------Authentication Routes---------  */}

        {/* -----------Common Routes---------  */}

        <PublicRoutes
          exact
          current={'unsubscribe'}
          component={UnsubscribeView}
          path="/unsubscribe"
          layout={Minimal}
        />
        <PublicRoutes
          exact
          current={'privacy'}
          component={PrivacyPolicyView}
          path="/privacy"
          layout={isLoggedIn ? Dashboard : PublicFluid}
        />
        <PublicRoutes
          exact
          current={'tos'}
          component={PrivacyPolicyView}
          path="/tos"
          layout={isLoggedIn ? Dashboard : PublicFluid}
        />

        {/* -----------Employer Routes---------  */}
        <PublicRoutes
          exact
          current={'home'}
          component={HomeView}
          path="/"
          layout={PublicFluid}
        />
        <AuthenticatedRoutes
          exact
          current={'profileSettings'}
          component={ProfileSettingsView}
          path="/profile"
          layout={Dashboard}
        />
        <AuthenticatedRoutes
          exact
          current={'integrations'}
          component={IntegrationsView}
          path="/integrations"
          layout={Dashboard}
        />
        <AuthenticatedRoutes
          exact
          current={'preferences'}
          component={PreferencesView}
          path="/preferences"
          layout={Dashboard}
        />
        <AuthenticatedRoutes
          exact
          current={'deepLinkValidation'}
          component={DeepLinkValidationView}
          path="/interaction"
          layout={Dashboard}
        />
        <PublicRoutes
          exact
          current={'googleCalendarConnect'}
          component={GoogleCalendarConnectView}
          path="/oauth/google"
          layout={PublicFluid}
          noFooter
        />
        <PublicRoutes
          exact
          current={'gmailIntegrationHelp'}
          component={GmailIntegrationHelpView}
          path="/google-oauth-integration-help"
          layout={PublicFluid}
          noFooter
        />
        <PublicRoutes
          exact
          current={'openAiIntegrationHelp'}
          component={OpenAiIntegrationHelpView}
          path="/openai-integration-help"
          layout={PublicFluid}
          noFooter
        />
        <PublicRoutes
          exact
          current={'zoomConnect'}
          component={ZoomOAuthConnectView}
          path="/oauth/zoom"
          layout={PublicFluid}
          noFooter
        />
        <PublicRoutes
          exact
          current={'pipedriveConnect'}
          component={PipedriveOAuthConnectView}
          path="/oauth/pipedrive-crm"
          layout={PublicFluid}
          noFooter
        />
        <PublicRoutes
          exact
          current={'outlookConnect'}
          component={OutlookEmailOAuthConnectView}
          path="/oauth/outlook/email"
          layout={PublicFluid}
          noFooter
        />
        <PublicRoutes
          exact
          current={'outlookCalendarConnect'}
          component={OutlookCalendarOAuthConnectView}
          path="/oauth/outlook/calendar"
          layout={PublicFluid}
          noFooter
        />
        <PublicRoutes
          exact
          current={'gmailConnect'}
          component={GmailOAuthConnectView}
          path="/oauth/google/gmail"
          layout={PublicFluid}
          noFooter
        />
        <PublicRoutes
          exact
          current={'slackConnect'}
          component={SlackOAuthConnectView}
          path="/oauth/slack"
          layout={PublicFluid}
          noFooter
        />
        <PublicRoutes
          exact
          current={'zohoCrmConnect'}
          component={ZohoCrmConnectView}
          path="/oauth/zoho-crm"
          layout={PublicFluid}
          noFooter
        />
        <PublicRoutes
          exact
          current={'oauthLogin'}
          component={OAuthLoginView}
          path="/oauth/login"
          layout={PublicFluid}
          noFooter
        />
        <PublicRoutes
          exact
          current={'oauthConsent'}
          component={OAuthConsentView}
          path="/oauth/consent"
          layout={PublicFluid}
          noFooter
        />
        <PublicRoutes
          exact
          current={'unipileSuccess'}
          component={UnipileOAuthConnectView}
          path="/integrations/unipile/success"
          layout={PublicFluid}
          noFooter
        />
        <PublicRoutes
          exact
          current={'unipileFailure'}
          component={UnipileOAuthConnectView}
          path="/integrations/unipile/failure"
          layout={PublicFluid}
          noFooter
        />
        <AuthenticatedRoutes
          exact
          current={'tools'}
          component={RecallAlternativeView}
          path="/m-record"
          layout={Dashboard}
        />
        <AuthenticatedRoutes
          exact
          current={'teamSettings'}
          component={TeamSettingsView}
          path="/manage-members"
          layout={Dashboard}
        />
        <AuthenticatedRoutes
          exact
          current={'orgSettings'}
          component={OrgSettingsView}
          path="/org-settings"
          layout={Dashboard}
        />
        <AuthenticatedRoutes
          exact
          current={'salesOrg'}
          component={SalesOrgView}
          path="/sales-org"
          layout={Dashboard}
        />

        {/* -----------Deal Copilot Routes---------  */}
        <AuthenticatedRoutes
          exact
          current={'mailbox'}
          component={MailboxView}
          path="/mailbox"
          layout={Dashboard}
        />
        <AuthenticatedRoutes
          exact
          current={'actionPlan'}
          component={ActionPlanView}
          path="/action-plan"
          layout={Dashboard}
        />
        <PublicRoutes
          exact
          current={'actionPlan'}
          component={MeetingPrepPublicView}
          path="/meeting-prep"
          layout={isLoggedIn ? Dashboard : Minimal}
        />
        <AuthenticatedRoutes
          exact
          current={'dealsView'}
          component={DealsView}
          path="/deals"
          layout={Dashboard}
        />
        <AuthenticatedRoutes
          exact
          current={'unlinkedItems'}
          component={UnlinkedItemsView}
          path="/unlinked-items"
          layout={Dashboard}
        />
        <AuthenticatedRoutes
          exact
          current={'dealPageView'}
          component={DealPageView}
          path="/deals/:dealId"
          layout={Dashboard}
        />
        <AuthenticatedRoutes
          exact
          current={'companies'}
          component={CompaniesView}
          path="/companies"
          layout={Dashboard}
        />
        <AuthenticatedRoutes
          exact
          current={'companyPage'}
          component={CompanyPageView}
          path="/companies/:companyId"
          layout={Dashboard}
        />
        <AuthenticatedRoutes
          exact
          current={'contacts'}
          component={ContactsView}
          path="/contacts"
          layout={Dashboard}
        />
        <AuthenticatedRoutes
          exact
          current={'contactPage'}
          component={ContactPageView}
          path="/contacts/:contactId"
          layout={Dashboard}
        />
        <AuthenticatedRoutes
          exact
          current={'reports'}
          component={ReportsView}
          path="/reports"
          layout={Dashboard}
        />
        <PublicRoutes
          exact
          current={'featurePreview'}
          noTopbar
          component={FeaturePreviewView}
          path="/fp/:uuid"
          layout={PublicFluid}
        />
        <AuthenticatedRoutes
          exact
          current={'digitalTwin'}
          component={ExpertConversationView}
          path="/digital-twin"
          layout={Dashboard}
        />
        <AuthenticatedRoutes
          exact
          current={'askAI'}
          component={ExpertConversationView}
          path="/ask-ai"
          layout={Dashboard}
        />
        <AuthenticatedRoutes
          exact
          current={'pricing'}
          component={PricingView}
          path="/pricing"
          layout={Dashboard}
        />
        <AuthenticatedRoutes
          exact
          current={'conversationAi'}
          component={ExpertConversationView}
          path="/conversation-ai"
          layout={Dashboard}
        />
        {/* -----------Deal Copilot Routes---------  */}

        {/* -----------Other Routes---------  */}
        <PublicRoutes
          exact
          current={'not-found-cover'}
          component={NotFoundCoverView}
          path="/not-found-cover"
          layout={Minimal}
        />

        <PublicRoutes
          exact
          current={'something-went-wrong'}
          component={SomethingWentWrongView}
          path="/something-went-wrong"
          layout={Minimal}
        />
        <Redirect to={'/not-found-cover'} status={404} />
        {/* -----------Other Routes---------  */}
      </Switch>
    </Suspense>
  );
};

export default Routes;
